import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useConfig } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Folders, Plus, PencilSimple, Trash, ArrowSquareOut, GoogleDriveLogo, Trash as TrashIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import DriveStatusCard from "@/components/DriveStatusCard";

const badgeCls = {
  drive: "bg-blue-50 text-blue-800 border-blue-200",
  manual: "bg-gray-100 text-gray-800 border-gray-200",
  demo: "bg-amber-50 text-amber-900 border-amber-200",
};

export default function Admin() {
  const cfg = useConfig();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", drive_url: "", content: "" });

  const load = () => {
    setLoading(true);
    api.get("/documents").then(r => setDocs(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm({ name: "", drive_url: "", content: "" }); setOpen(true); };
  const openEdit = (d) => {
    if (d.source === "drive") { toast.info("Drive documents are read-only. Edit the original file in Google Drive."); return; }
    setEditing(d); setForm({ name: d.name, drive_url: d.drive_url || "", content: d.content }); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.content.trim()) { toast.error("Name and content are required"); return; }
    try {
      if (editing) await api.put(`/documents/${editing.id}`, form);
      else await api.post("/documents", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (d) => {
    if (d.source === "drive") {
      toast.info("Drive documents are removed from the knowledge base by deleting the original file in Google Drive, then re-syncing.");
      return;
    }
    if (!window.confirm("Remove this document from the knowledge base?")) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const purgeDemo = async () => {
    if (!window.confirm("Remove ALL demo/test documents from the knowledge base? This is intended for pre-production cleanup.")) return;
    const { data } = await api.post("/documents/demo/purge");
    toast.success(`Removed ${data.deleted} demo documents`);
    load();
  };

  const demoCount = docs.filter(d => d.source === "demo").length;

  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl mx-auto w-full">
      <div className="flex items-center gap-3 mb-2">
        <Folders size={22} weight="duotone" />
        <h1 className="font-heading text-4xl">Knowledge base</h1>
      </div>
      <p className="text-ink-soft mb-6">
        Approved training materials that ground every Ask Tracey answer. Google Drive is the source of truth — new, changed, and deleted files sync automatically.
      </p>

      <DriveStatusCard />

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Button onClick={openNew} data-testid="add-doc-btn" className="rounded-full bg-gk-red hover:bg-gk-red-bright" disabled={cfg.drive_configured}>
          <Plus size={16} weight="bold" className="mr-1.5" /> Add manual document
        </Button>
        {demoCount > 0 && (
          <Button variant="outline" onClick={purgeDemo} data-testid="purge-demo-btn"
            className="rounded-full border-amber-400 text-amber-800 hover:bg-amber-50">
            <TrashIcon size={14} weight="duotone" className="mr-1.5" /> Purge {demoCount} demo doc{demoCount === 1 ? "" : "s"}
          </Button>
        )}
        {cfg.drive_configured && (
          <span className="text-xs text-ink-soft ml-1">
            Drive is connected — manage knowledge by editing the source folder in Google Drive.
          </span>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit document" : "Add manual document"}</DialogTitle>
            <DialogDescription className="text-ink-soft">
              Ad-hoc supplement to Tracey's Drive-sourced knowledge base. Managers only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Document name</Label>
              <Input data-testid="doc-name" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>Reference link (optional)</Label>
              <Input data-testid="doc-url" value={form.drive_url} onChange={(e)=>setForm({...form, drive_url: e.target.value})}
                placeholder="https://…" className="mt-1" />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea data-testid="doc-content" value={form.content} onChange={(e)=>setForm({...form, content: e.target.value})}
                rows={10} className="mt-1 font-body" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="doc-save" className="bg-gk-red hover:bg-gk-red-bright">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="text-ink-soft">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="text-ink-soft py-16 text-center border border-dashed border-line rounded-2xl">
          {cfg.drive_configured
            ? "No documents indexed yet. Run a sync above to pull from Google Drive."
            : "No documents yet. Connect Google Drive or add a manual document to seed the knowledge base."}
        </div>
      ) : (
        <div className="border border-line rounded-2xl overflow-hidden bg-surface/40" data-testid="docs-table">
          <div className="hidden md:grid grid-cols-[1fr_100px_180px_100px] gap-4 px-4 py-2.5 text-[11px] uppercase tracking-widest text-ink-soft border-b border-line bg-surface">
            <div>Document</div><div>Source</div><div>Last update</div><div className="text-right">Actions</div>
          </div>
          <ul className="divide-y divide-line">
            {docs.map((d) => (
              <li key={d.id} className="grid grid-cols-1 md:grid-cols-[1fr_100px_180px_100px] gap-2 md:gap-4 px-4 py-3 items-center">
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    {d.source === "drive" && <GoogleDriveLogo size={14} weight="duotone" className="text-blue-700 shrink-0" />}
                    <span className="truncate">{d.name}</span>
                  </div>
                  {d.drive_url && (
                    <a href={d.drive_url} target="_blank" rel="noreferrer" className="text-xs text-ink-soft hover:text-gk-red inline-flex items-center gap-1">
                      <ArrowSquareOut size={11} weight="bold" /> Open source
                    </a>
                  )}
                  {d.index_status && d.index_status !== "indexed" && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-900">{d.index_status}</span>
                  )}
                </div>
                <div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${badgeCls[d.source] || badgeCls.manual}`}>{d.source}</span>
                </div>
                <div className="text-xs text-ink-soft">{new Date(d.updated_at || d.created_at).toLocaleString()}</div>
                <div className="flex md:justify-end gap-2">
                  <button onClick={()=>openEdit(d)} data-testid={`edit-${d.id}`} disabled={d.source === "drive"}
                    className="p-2 rounded-lg hover:bg-surface text-ink-soft hover:text-gk-charcoal disabled:opacity-40 disabled:cursor-not-allowed">
                    <PencilSimple size={16} weight="duotone" />
                  </button>
                  <button onClick={()=>remove(d)} data-testid={`del-${d.id}`} disabled={d.source === "drive"}
                    className="p-2 rounded-lg hover:bg-surface text-ink-soft hover:text-gk-red disabled:opacity-40 disabled:cursor-not-allowed">
                    <Trash size={16} weight="duotone" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
